import { createContext, useContext } from "react";

import { useGetUnreadNotificationsCount } from "@/hooks/queries/useGetUnreadNotificationsCount";
import { NotificationType } from "@/types";

interface UnreadNotificationsContextValue {
  unreadCount: number;
  // Absent types have no unread notifications
  unreadCountsByType: Partial<Record<NotificationType, number>>;
  // The five-star slice of the unread EXCELLENT_CANDIDATE count
  unreadExcellentCandidateCount: number;
  // ISO timestamp of the oldest unread notification of each type
  oldestUnreadAtByType: Partial<Record<NotificationType, string>>;
}

const UnreadNotificationsContext = createContext<
  UnreadNotificationsContextValue | undefined
>(undefined);

export const UnreadNotificationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data } = useGetUnreadNotificationsCount();

  return (
    <UnreadNotificationsContext.Provider
      value={{
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        unreadCount: data?.total || 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        unreadCountsByType: data?.byType || {},
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        unreadExcellentCandidateCount: data?.excellentCandidateCount || 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        oldestUnreadAtByType: data?.oldestUnreadAtByType || {},
      }}
    >
      {children}
    </UnreadNotificationsContext.Provider>
  );
};

export const useUnreadNotifications = () => {
  const context = useContext(UnreadNotificationsContext);

  // Support hot reload without crashing - Vite issue
  if (!context) {
    if (import.meta.env.DEV) {
      return {
        unreadCount: 0,
        unreadCountsByType: {},
        unreadExcellentCandidateCount: 0,
        oldestUnreadAtByType: {},
      };
    }

    throw new Error(
      "useUnreadNotifications must be used within a UnreadNotificationsProvider",
    );
  }
  return context;
};
